import { getApiEndpoint } from '../config/apiEndpoints';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface AuthTokens {
  access: string;
  refresh: string;
  user: {
    uuid: string;
    username: string;
    email: string;
    is_staff: boolean;
    is_active: boolean;
    date_joined: string;
  };
}

export interface SignupRequest {
  username: string;
  email: string;
  password1: string;
  password2: string;
  terms_of_service: boolean;
  privacy_policy: boolean;
  cookie_policy: boolean;
  [key: string]: any;
}

export interface SignupResponse {
  status?: string;
  message?: string;
  access?: string;
  refresh?: string;
  token_type?: string;
  expires_in?: number;
  user?: {
    uuid: string;
    username: string;
    email: string;
    is_staff?: boolean;
    is_active?: boolean;
    date_joined?: string;
  };
}

export interface RefreshTokenResponse {
  access: string;
  refresh?: string;
}

export interface LogoutResponse {
  status: string;
  message: string;
  redirect_url: string;
}

export interface PasswordResetRequest {
  username: string;
}

export interface PasswordResetResponse {
  status: string;
  message: string;
}

export interface ResetPasswordRequest {
  reset_token: string;
  new_password: string;
}

export interface ResetPasswordResponse {
  status: string;
  message: string;
}

export interface AuthError {
  error: string;
  message?: string;
}

const authEndpoint = (): string => getApiEndpoint('AUTH', '/auth');

export const loginWithLambda = async (credentials: LoginRequest): Promise<AuthTokens | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_type: 'login',
        username: credentials.username,
        password: credentials.password,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as AuthTokens;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to login' };
  }
};

export const signupWithLambda = async (signupData: SignupRequest): Promise<SignupResponse | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_type: 'signup',
        ...signupData,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as SignupResponse;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to signup' };
  }
};

export const refreshTokenWithLambda = async (refreshToken: string): Promise<RefreshTokenResponse | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        request_type: 'refresh_token',
        refresh: refreshToken,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as RefreshTokenResponse;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to refresh token' };
  }
};

export const logoutWithLambda = async (): Promise<LogoutResponse | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_type: 'logout' }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as LogoutResponse;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to logout' };
  }
};

export const requestPasswordReset = async (
  payload: PasswordResetRequest
): Promise<PasswordResetResponse | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_type: 'request_password_reset', ...payload }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as PasswordResetResponse;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to request password reset' };
  }
};

export const resetPassword = async (
  payload: ResetPasswordRequest
): Promise<ResetPasswordResponse | AuthError> => {
  try {
    const response = await fetch(authEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ request_type: 'reset_password', ...payload }),
    });

    const data = await response.json();

    if (!response.ok) {
      const message = data.error || data.message || `HTTP ${response.status}: ${response.statusText}`;
      return { error: message };
    }

    return data as ResetPasswordResponse;
  } catch (error) {
    return { error: error instanceof Error ? error.message : 'Failed to reset password' };
  }
};
